/**
 * 시공일정 **초안 생성기**. 공정별 「며칠」을 착공일 기준으로 날짜에 펼친다.
 *
 * 규칙 엔진이 아니라 생성기다 — 한 번 펼치면 날짜가 진짜 데이터가 되고, 그 뒤로는
 * 담당자가 칸을 직접 고친다. 그래서 여기에는 겹침 검사도 순서 검사도 없다. 타일과
 * 도배가 겹칠 수 있는지는 공간·층·인원이 걸린 **현장 판단**이라 계산으로 못 구한다.
 *
 * 날짜는 전부 `YYYY-MM-DD` 문자열이고 계산은 **UTC 자정**으로 한다. 현지 시각으로
 * 다루면 서버 타임존에 따라 하루가 밀린다 — 견적서에 찍히는 날짜가 서버 위치에
 * 따라 달라지면 안 된다.
 */

import { subjectJosa } from "../../lib/josa";

const DAY_MS = 86_400_000;

/** 한 견적이 담을 수 있는 최장 기간. 10년을 넘기면 입력 실수다. */
const MAX_SPAN_DAYS = 3650;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** 토·일·공휴일 중 **작업하는** 날. 체크 안 하면 쉰다. */
export interface WorkdayRule {
  saturday: boolean;
  sunday: boolean;
  holiday: boolean;
}

/** 아무것도 체크하지 않은 상태 — 토·일·공휴일을 모두 쉰다. */
export const WORK_NONE: WorkdayRule = {
  saturday: false,
  sunday: false,
  holiday: false,
};

export interface PhaseDuration {
  phase_id: string;
  /** 작업일 수. 0 이하면 「안 쓰는 공정」이라 결과에서 빠진다. */
  duration_days: number;
}

export interface PhaseDates {
  phase_id: string;
  start_date: string;
  end_date: string;
}

export interface ScheduleSummary {
  start_date: string;
  end_date: string;
  /** 공정 구간에 든 **작업일의 가짓수**. 겹친 날은 한 번만 센다. */
  total_workdays: number;
}

/**
 * `YYYY-MM-DD` → UTC 자정 밀리초.
 *
 * `Date.parse` 에만 기대지 않는다 — `2026-02-31` 은 자바스크립트가 3월 3일로
 * 조용히 굴려 버려서, 잘못 찍은 날짜가 아무 말 없이 다른 날이 된다.
 */
const parseDay = (value: string, label: string): number => {
  if (!DATE_RE.test(value)) {
    throw new Error(`${label} 형식이 올바르지 않습니다 (YYYY-MM-DD)`);
  }
  const [y, m, d] = value.split("-").map(Number);
  // 연도 4자리를 넘으면 `toISOString` 이 `+010000-…` 을 내놓아 `slice(0, 10)` 이
  // 깨진다. 애초에 그런 날짜가 들어올 일이 없으니 여기서 돌려보낸다.
  if (y < 1900 || y > 9000) {
    throw new Error(`${label}${subjectJosa(label)} 다룰 수 있는 범위를 벗어났습니다`);
  }
  const ms = Date.UTC(y, m - 1, d);
  const back = new Date(ms);
  if (
    back.getUTCFullYear() !== y ||
    back.getUTCMonth() !== m - 1 ||
    back.getUTCDate() !== d
  ) {
    throw new Error(`${label}${subjectJosa(label)} 달력에 없는 날짜입니다`);
  }
  return ms;
};

/**
 * 착공일 한 칸을 미리 본다. 형식·달력·연도 범위를 `generateSchedule` 과 **같은
 * 자로** 검사한다 — 부르는 쪽이 값비싼 일(외부 API 조회)을 하기 전에 거르라고
 * 열어 둔 문이다.
 */
export const assertStartDate = (value: string): void => {
  parseDay(value, "착공일");
};

const formatDay = (ms: number): string =>
  new Date(ms).toISOString().slice(0, 10);

const isWorkday = (
  ms: number,
  work: WorkdayRule,
  holidays: ReadonlySet<string>
): boolean => {
  const dow = new Date(ms).getUTCDay();
  if (dow === 6 && !work.saturday) return false;
  if (dow === 0 && !work.sunday) return false;
  // 주말이면 위에서 이미 걸렸다 — 공휴일이 주말과 겹쳐도 이중으로 밀지 않는다.
  if (!work.holiday && holidays.has(formatDay(ms))) return false;
  return true;
};

// 공정표와 같은 자로 `YYYY-MM-DD` 하루가 작업일인지 — 매일 알림 cron 이 쓴다.
export const isWorkdayOn = (
  date: string,
  work: WorkdayRule,
  holidays: ReadonlySet<string>
): boolean => isWorkday(parseDay(date, "날짜"), work, holidays);

/** `ms` 를 포함해 그날부터 처음 만나는 작업일. */
const workdayFrom = (
  ms: number,
  work: WorkdayRule,
  holidays: ReadonlySet<string>
): number => {
  for (let i = 0; i <= MAX_SPAN_DAYS; i += 1) {
    const day = ms + i * DAY_MS;
    if (isWorkday(day, work, holidays)) return day;
  }
  throw new Error("쉬는 날만 이어져 시작할 수 있는 날이 없습니다");
};

const nextWorkdayAfter = (
  ms: number,
  work: WorkdayRule,
  holidays: ReadonlySet<string>
): number => workdayFrom(ms + DAY_MS, work, holidays);

export interface GenerateScheduleInput {
  /** 예상 착공일. 이 날이 쉬는 날이면 다음 작업일로 밀어서 시작한다. */
  start_date: string;
  /** 공정 순서대로. 초안은 **순차로** 깐다 — 겹치게 두려면 생성 뒤 손으로 고친다. */
  plans: readonly PhaseDuration[];
  work: WorkdayRule;
  /** `YYYY-MM-DD` 집합. 조회에 실패했으면 빈 집합을 준다. */
  holidays: ReadonlySet<string>;
}

/**
 * 공정별 「며칠」을 날짜로 펼친다. 앞 공정이 끝난 **다음 작업일**에 다음 공정이 선다.
 *
 * 며칠을 안 채운 공정은 결과에서 빠지고, 날짜를 밀지도 않는다 — 안 쓰는 공정이
 * 뒤 공정을 미루면 준공일이 조용히 틀어진다.
 */
export const generateSchedule = ({
  start_date,
  plans,
  work,
  holidays,
}: GenerateScheduleInput): PhaseDates[] => {
  const origin = parseDay(start_date, "착공일");

  const rows: PhaseDates[] = [];
  let cursor = origin;

  for (const plan of plans) {
    // 소수점은 올린다. 반나절짜리 공정도 달력에서는 하루를 차지한다.
    const days = Math.ceil(plan.duration_days);
    if (!Number.isFinite(days) || days < 1) continue;
    if (days > MAX_SPAN_DAYS) {
      throw new Error("공사 기간이 너무 깁니다 — 며칠을 확인해 주세요");
    }

    const startMs = workdayFrom(cursor, work, holidays);
    let endMs = startMs;
    for (let i = 1; i < days; i += 1) {
      endMs = nextWorkdayAfter(endMs, work, holidays);
    }

    if (endMs - origin > MAX_SPAN_DAYS * DAY_MS) {
      throw new Error("공사 기간이 너무 깁니다 — 며칠을 확인해 주세요");
    }

    rows.push({
      phase_id: plan.phase_id,
      start_date: formatDay(startMs),
      end_date: formatDay(endMs),
    });
    cursor = nextWorkdayAfter(endMs, work, holidays);
  }

  return rows;
};

/**
 * 견적서 머리에 찍을 한 줄 — 착공일 · 준공일 · 총 작업일.
 *
 * 총 작업일은 **합이 아니라 가짓수**다. 손으로 고쳐 공정이 겹쳐 있으면 합은 실제
 * 현장에 있는 날수보다 부풀어 오른다.
 *
 * 채운 공정이 없으면 null 이고, 화면은 일정 섹션 자체를 빼야 한다.
 */
export const summarizeSchedule = (
  rows: readonly PhaseDates[],
  work: WorkdayRule,
  holidays: ReadonlySet<string>
): ScheduleSummary | null => {
  if (rows.length === 0) return null;

  const workdays = new Set<string>();
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  for (const row of rows) {
    const a = parseDay(row.start_date, "공정 시작일");
    const b = parseDay(row.end_date, "공정 종료일");
    // 손으로 고치다 거꾸로 넣은 칸이 있어도 터지지 않게 범위로만 본다.
    const from = Math.min(a, b);
    const to = Math.max(a, b);
    if (to - from > MAX_SPAN_DAYS * DAY_MS) {
      throw new Error("공사 기간이 너무 깁니다 — 날짜를 확인해 주세요");
    }

    min = Math.min(min, from);
    max = Math.max(max, to);
    for (let day = from; day <= to; day += DAY_MS) {
      if (isWorkday(day, work, holidays)) workdays.add(formatDay(day));
    }
  }

  return {
    start_date: formatDay(min),
    end_date: formatDay(max),
    total_workdays: workdays.size,
  };
};
